import React, { useCallback, useEffect, useMemo } from "react";
import { View, StyleSheet, ViewStyle, Pressable } from "react-native";
import { useTheme } from "../hooks/use-theme";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import Icon from "./Icon";

const ICON_SIZE = 16;
const BADGE_SIZE = 24;

type OptionBadgeProps = {
    badge: string;
    isSelected: boolean;
    disabled?: boolean;
    showCheckIcon?: boolean;
};

const DEFAULT_BACKGROUND = "transparent";

const OptionBadge = ({ badge, isSelected, disabled }: OptionBadgeProps) => {
    const { colors } = useTheme();
    const styles = useStyles();
    const color = useSharedValue(isSelected ? colors.onPrimary : colors.primary);

    const animatedStyles = useAnimatedStyle(() => ({
        color: color.value,
    }));

    useEffect(() => {
        color.value = withTiming(isSelected ? colors.onPrimary : colors.primary);
    }, [color, colors.background, colors.onPrimary, colors.primary, isSelected]);

    return (
        <Animated.Text
            style={[styles.badge, animatedStyles, disabled && !isSelected && styles.disabledText]}
            adjustsFontSizeToFit={true}
            numberOfLines={1}
        >
            {badge}
        </Animated.Text>
    );
};

type OptionTextProps = {
    label: string;
    isSelected: boolean;
    disabled?: boolean;
    showCheckIcon?: boolean;
};

const OptionText = ({ label, isSelected, disabled, showCheckIcon }: OptionTextProps) => {
    const { colors, gridSize } = useTheme();
    const styles = useStyles();

    const textColor = disabled
        ? isSelected
            ? colors.greyAccessible
            : colors.fieldLabel
        : isSelected
          ? colors.onPrimary
          : colors.primary;

    const color = useSharedValue(textColor);

    const animatedStyles = useAnimatedStyle(() => ({
        color: color.value,
    }));

    const showCheckIconStyle = showCheckIcon ? {} : { paddingLeft: gridSize / 3 };

    useEffect(() => {
        color.value = withTiming(textColor);
    }, [color, textColor, colors.primary]);

    return (
        <Animated.Text
            style={[
                styles.text,
                animatedStyles,
                disabled && !isSelected && styles.disabledText,
                showCheckIconStyle,
            ]}
            adjustsFontSizeToFit={true}
            numberOfLines={1}
        >
            {label}
        </Animated.Text>
    );
};

type SelectedIconProps = {
    isSelected: boolean;
    disabled?: boolean;
};

const SelectedIcon = ({ isSelected, disabled = false }: SelectedIconProps) => {
    const styles = useStyles();
    const { colors } = useTheme();

    const selectedColor = disabled ? colors.fieldLabel : colors.onPrimary;
    const selectedColorHigh = disabled ? colors.fieldLabel : colors.primary;

    const borderColor = useSharedValue(isSelected ? DEFAULT_BACKGROUND : selectedColorHigh);
    const backgroundColor = useSharedValue(isSelected ? selectedColor : DEFAULT_BACKGROUND);

    useEffect(() => {
        backgroundColor.value = withTiming(isSelected ? colors.primary : DEFAULT_BACKGROUND);
        borderColor.value = withTiming(isSelected ? DEFAULT_BACKGROUND : selectedColorHigh);
    }, [
        backgroundColor,
        selectedColor,
        isSelected,
        colors.primary,
        borderColor,
        selectedColorHigh,
    ]);

    return (
        <Animated.View
            style={[styles.checkIconContainer, disabled && styles.checkIconContainerDisabled]}
        >
            <Icon
                name={isSelected ? "check-solid" : "circle"}
                size={ICON_SIZE}
                style={styles.icon}
                color={
                    isSelected
                        ? disabled
                            ? colors.lighterGrey
                            : colors.background
                        : colors.primary
                }
            />
        </Animated.View>
    );
};

export type OptionType<V> = {
    label: string;
    value: V;
    badge?: string;
};

export type OptionsSelectorProps<V> = {
    data: OptionType<V>[];
    value: V;
    style?: ViewStyle;
    onSelect?: (option: V) => void;
    showCheckIcon?: boolean;
    disabled?: boolean;
    shrinked?: boolean;
};

type OptionItemProp = {
    isSelected: boolean;
    label: string;
    badge?: string;
    onSelect: () => void;
    disabled?: boolean;
    showCheckIcon?: boolean;
    shrinked?: boolean;
};
const OptionItem: React.FC<OptionItemProp> = ({
    isSelected,
    label,
    badge,
    onSelect,
    disabled = false,
    showCheckIcon = true,
    shrinked,
}) => {
    const styles = useStyles();
    const { colors } = useTheme();

    const selectedColor = disabled ? colors.lighterGrey : colors.primary;

    const getBackgroundColor = useCallback(() => {
        return isSelected ? selectedColor : DEFAULT_BACKGROUND;
    }, [isSelected, selectedColor]);

    const getBorderColor = useCallback(() => {
        return isSelected ? selectedColor : colors.accentLighter;
    }, [colors.accentLighter, isSelected, selectedColor]);

    const backgroundColor = useSharedValue(getBackgroundColor());
    const borderColor = useSharedValue(getBorderColor());
    const animatedStyles = useAnimatedStyle(() => ({
        backgroundColor: backgroundColor.value,
        borderColor: borderColor.value,
    }));

    useEffect(() => {
        backgroundColor.value = withTiming(getBackgroundColor());
        borderColor.value = withTiming(getBorderColor());
    }, [
        backgroundColor,
        selectedColor,
        isSelected,
        getBackgroundColor,
        borderColor,
        getBorderColor,
    ]);
    return (
        <Pressable
            style={!shrinked && styles.optionPressable}
            onPress={onSelect}
            disabled={disabled}
            accessibilityRole="radio"
            accessibilityState={{ checked: isSelected, disabled }}
        >
            <Animated.View
                style={[styles.option, animatedStyles, disabled && styles.disabledOption]}
            >
                {showCheckIcon && <SelectedIcon isSelected={isSelected} disabled={disabled} />}
                <OptionText
                    label={label}
                    isSelected={isSelected}
                    disabled={disabled}
                    showCheckIcon={showCheckIcon}
                />
                {badge !== undefined && (
                    <OptionBadge badge={badge} isSelected={isSelected} disabled={disabled} />
                )}
            </Animated.View>
        </Pressable>
    );
};

const OptionsSelector = <V,>({
    style,
    data,
    value,
    onSelect,
    ...props
}: OptionsSelectorProps<V>) => {
    const styles = useStyles();
    return (
        <View style={[style]}>
            <View style={styles.containerView}>
                {data.map((option, index) => (
                    <OptionItem
                        key={index}
                        isSelected={option.value === value}
                        label={option.label}
                        onSelect={() => {
                            onSelect?.(option.value);
                        }}
                        badge={option.badge}
                        {...props}
                    />
                ))}
            </View>
        </View>
    );
};

const useStyles = () => {
    const { colors, fonts, gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                // options that do not fit on one line wrap onto the next one
                containerView: {
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: Math.round(gridSize / 2),
                    height: "auto",
                },
                option: {
                    flexGrow: 1,
                    position: "relative",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "row",
                    borderRadius: 100,
                    borderWidth: 3,
                    paddingVertical: gridSize / 3,
                },
                optionPressable: {
                    flexGrow: 1,
                },
                disabledOption: {
                    borderColor: colors.lighterGrey,
                },
                text: {
                    ...fonts.bodyLarge,
                    paddingLeft: ICON_SIZE + gridSize,
                    paddingRight: gridSize / 1.5,
                    textAlign: "center",
                    textAlignVertical: "center",
                    lineHeight: undefined,
                    flexShrink: 1,
                },
                badge: {
                    ...fonts.bodyMedium,
                    width: BADGE_SIZE,
                    textAlign: "center",
                    textAlignVertical: "center",
                    lineHeight: undefined,
                },
                disabledText: {
                    color: colors.backdrop,
                },
                checkIconContainer: {
                    position: "absolute",
                    left: 8,
                    backgroundColor: "transparent",
                },
                checkIconContainerDisabled: {
                    borderColor: colors.fieldLabel,
                },
                icon: {
                    margin: 3,
                },
            }),
        [
            colors.lighterGrey,
            colors.backdrop,
            colors.fieldLabel,
            gridSize,
            fonts.bodyLarge,
            fonts.bodyMedium,
        ],
    );
};

export default OptionsSelector;
